import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, CardHeader, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { asText } from "@/hq/asText";
import HQActivityFeed from "@/hq/campaigns/HQActivityFeed";

const STAGE_LABEL = {
  NEW: "New", RESEARCHING: "Researching", SCANNED: "Scanned",
  QUALIFIED: "Qualified", CAMPAIGN_READY: "Campaign Ready",
  CONTACTED: "Contacted", ENGAGED: "Engaged",
  OPPORTUNITY: "Opportunity", QUOTE: "Quote", WON: "Won",
  LOST: "Lost", DO_NOT_CONTACT: "Do Not Contact",
};

function CurrencyRow({ label, values }) {
  const entries = Object.entries(values || {});
  if (!entries.length) {
    return (
      <div className="text-[13px] text-[#8A8A8A]" data-testid={`sales-dashboard-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        No open opportunities yet.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={`sales-dashboard-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      {entries.map(([cur, v]) => (
        <div key={cur} className="px-3 py-2 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7]">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">{cur}</div>
          <div className="font-black text-lg leading-none mt-1">{Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
      ))}
    </div>
  );
}

export default function HQSalesDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pitchPerf, setPitchPerf] = useState(null);
  const [pitchDays, setPitchDays] = useState(30);
  useEffect(() => {
    let alive = true;
    hqApi.get("/sales/dashboard").then((r) => alive && setData(r.data)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    hqApi.get("/control/analytics/pitch-performance", { params: { days: pitchDays } })
      .then((r) => alive && setPitchPerf(r.data))
      .catch(() => alive && setPitchPerf(null));
    return () => { alive = false; };
  }, [pitchDays]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="sales-dashboard-loading">
        <Skeleton h={80} />
        <Skeleton h={200} />
      </div>
    );
  }
  if (!data) return <EmptyState title="Nothing yet" hint="Add prospects to start using the Sales Engine." testId="sales-dashboard-empty" />;

  return (
    <div className="space-y-6" data-testid="sales-dashboard-root">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="p-5" data-testid="sales-tile-total-prospects">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#8A8A8A]">Total Prospects</div>
          <div className="mt-2 font-black text-3xl">{data.prospects?.total || 0}</div>
        </Card>
        <Card className="p-5" data-testid="sales-tile-active-campaigns">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#8A8A8A]">Active Campaigns</div>
          <div className="mt-2 font-black text-3xl">{data.campaigns?.active_count || 0}</div>
        </Card>
        <Card className="p-5" data-testid="sales-tile-total-sent">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#8A8A8A]">Emails Sent</div>
          <div className="mt-2 font-black text-3xl">{data.campaigns?.total_sent || 0}</div>
        </Card>
        <Card className="p-5" data-testid="sales-tile-engagement">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#8A8A8A]">Opens detected · Clicks</div>
          <div className="mt-2 font-black text-3xl">{data.campaigns?.total_opens || 0} · {data.campaigns?.total_clicks || 0}</div>
          <div className="mt-1 text-[10px] text-[#8A8A8A]">Opens are noisy — mail privacy tools can trigger them.</div>
        </Card>
      </div>

      <Card data-testid="pitch-performance-panel">
        <CardHeader
          eyebrow="Campaign Performance"
          title="Pitch & campaign performance"
          action={
            <div className="flex gap-1" data-testid="pitch-performance-range">
              {[
                { d: 7,  l: "7d" },
                { d: 30, l: "30d" },
                { d: 90, l: "90d" },
                { d: 0,  l: "All" },
              ].map(({ d, l }) => (
                <button
                  key={d}
                  onClick={() => setPitchDays(d)}
                  data-testid={`pitch-performance-range-${d}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${pitchDays === d ? "bg-[#FF6A00] text-black" : "bg-white border border-[#EAEAEA] text-[#8A8A8A] hover:text-[#0A0A0A]"}`}
                >{l}</button>
              ))}
            </div>
          }
        />
        <div className="px-6 pb-6 pt-2">
          {!pitchPerf ? (
            <Skeleton h={120} />
          ) : (
            <>
              {/* Phase 3I.4Q.1 — Headline unified totals (Sent / Opened /
                  Clicked / Interested) sourced from the canonical
                  core_communications ledger. Same source powers the
                  per-type breakdown below so numbers never drift. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="pitch-performance-headline">
                <div className="p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7]" data-testid="pitch-performance-totals-sent">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Sent</div>
                  <div className="font-black text-3xl tabular-nums">{pitchPerf.totals.sent}</div>
                </div>
                <div className="p-3 rounded-xl bg-white border border-[#EAEAEA]" data-testid="pitch-performance-totals-opened">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Opened</div>
                  <div className="font-black text-3xl tabular-nums">{pitchPerf.totals.opened}</div>
                  <div className="text-[10px] text-[#8A8A8A] font-bold">{pitchPerf.totals.open_rate}%</div>
                </div>
                <div className="p-3 rounded-xl bg-white border border-[#EAEAEA]" data-testid="pitch-performance-totals-clicked">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#8A8A8A] font-bold">Clicked</div>
                  <div className="font-black text-3xl tabular-nums">{pitchPerf.totals.clicked}</div>
                  <div className="text-[10px] text-[#8A8A8A] font-bold">{pitchPerf.totals.click_rate}%</div>
                </div>
                <div className="p-3 rounded-xl bg-[#EBFBF2] border border-[#B7EBCF]" data-testid="pitch-performance-totals-interested">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#0F7B3E] font-bold">Interested</div>
                  <div className="font-black text-3xl tabular-nums text-[#0F7B3E]">{pitchPerf.totals.interested || 0}</div>
                  <div className="text-[10px] text-[#0F7B3E] font-bold">{pitchPerf.totals.interested_rate || 0}%</div>
                </div>
              </div>
              {pitchPerf.totals.sent === 0 ? (
                <EmptyState title="No pitch sends in this window"
                  hint="Send a pitch from Clients → Client profile → Pitch to start seeing performance."
                  testId="pitch-performance-empty"/>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]" data-testid="pitch-performance-table">
                    <thead className="text-[10px] tracking-[0.14em] uppercase font-black text-[#8A8A8A]">
                      <tr>
                        <th className="text-left py-2">Pitch</th>
                        <th className="text-right py-2">Sent</th>
                        <th className="text-right py-2">Delivered</th>
                        <th className="text-right py-2">Opened</th>
                        <th className="text-right py-2">Open %</th>
                        <th className="text-right py-2">Clicked</th>
                        <th className="text-right py-2">Click %</th>
                        <th className="text-right py-2">Interested</th>
                        <th className="text-right py-2">Int. %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#EAEAEA]">
                      {pitchPerf.breakdown.map((row) => (
                        <tr key={row.pitch_type} data-testid={`pitch-performance-row-${row.pitch_type}`}>
                          <td className="py-2 font-black uppercase text-[11px] tracking-[0.14em] text-[#FF6A00]">
                            {row.pitch_type === "full" ? "Full Pitch" : row.pitch_type}
                          </td>
                          <td className="py-2 text-right tabular-nums">{row.sent}</td>
                          <td className="py-2 text-right tabular-nums">{row.delivered}</td>
                          <td className="py-2 text-right tabular-nums">{row.opened}</td>
                          <td className="py-2 text-right tabular-nums">{row.open_rate}%</td>
                          <td className="py-2 text-right tabular-nums">{row.clicked}</td>
                          <td className="py-2 text-right tabular-nums">{row.click_rate}%</td>
                          <td className="py-2 text-right tabular-nums text-[#0F7B3E]">{row.interested || 0}</td>
                          <td className="py-2 text-right tabular-nums text-[#0F7B3E]">{row.interested_rate || 0}%</td>
                        </tr>
                      ))}
                      <tr className="bg-[#FFF7EE]" data-testid="pitch-performance-row-totals">
                        <td className="py-2 font-black uppercase text-[11px] tracking-[0.14em]">Totals</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.sent}</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.delivered}</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.opened}</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.open_rate}%</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.clicked}</td>
                        <td className="py-2 text-right font-black tabular-nums">{pitchPerf.totals.click_rate}%</td>
                        <td className="py-2 text-right font-black tabular-nums text-[#0F7B3E]">{pitchPerf.totals.interested || 0}</td>
                        <td className="py-2 text-right font-black tabular-nums text-[#0F7B3E]">{pitchPerf.totals.interested_rate || 0}%</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="mt-3 text-[11px] text-[#8A8A8A]">
                    Sourced from the canonical communications ledger. Opens/clicks capture from send date onward — pitches sent before tracking was wired show 0 (backfill in Phase 3I.4Q.4).
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader eyebrow="Pipeline" title="Open pipeline value (by currency)" />
        <CardHeader eyebrow="Prospects" title="By pipeline stage" action={
          <Link to="/hq/sales/prospects" className="text-[11px] tracking-[0.24em] uppercase font-bold text-[#FF6A00] hover:underline" data-testid="sales-dashboard-view-prospects">
            View all →
          </Link>
        } />
        <div className="px-6 pb-6 pt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(STAGE_LABEL).map(([code, label]) => {
            const n = data.prospects?.by_stage?.[code] || 0;
            return (
              <div key={code} className="px-3 py-3 rounded-xl border border-[#EAEAEA] bg-white" data-testid={`sales-stage-${code}`}>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
                <div className="mt-1 font-black text-xl">{n}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <CardHeader eyebrow="Activity" title="Recent pitch &amp; campaign activity"/>
        <div className="px-2 pb-4 pt-2">
          <HQActivityFeed compact limit={10} showFilters={false} testIdPrefix="sales-dashboard-activity"/>
        </div>
      </Card>
    </div>
  );
}
